/**
 * Frontend-only Journey clock. NOT backend-authoritative domain time.
 *
 * 1. Valid IANA Journey timezone → calendar date/time in that zone.
 * 2. Otherwise → device/local zone (injectable in tests via deviceTimeZone).
 */

export function resolveIanaTimeZone(value) {
  const zone = String(value || '').trim();
  if (!zone) return null;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: zone }).format(new Date(0));
    return zone;
  } catch {
    return null;
  }
}

function partMap(parts) {
  const map = {};
  parts.forEach((part) => {
    if (part.type !== 'literal') map[part.type] = part.value;
  });
  return map;
}

export function formatCalendarDate(year, month, day) {
  const y = String(year).padStart(4, '0');
  const m = String(month).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatClockTime(hour, minute) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * @param {object} options
 * @param {string|null} [options.timezone] Journey IANA timezone
 * @param {Date|string|number} [options.now]
 * @param {string|null} [options.deviceTimeZone] test-only fallback zone; omit in production
 */
export function resolveJourneyClock({ timezone, now = new Date(), deviceTimeZone } = {}) {
  const instant = now instanceof Date ? now : new Date(now);
  const journeyZone = resolveIanaTimeZone(timezone);
  const deviceZone = resolveIanaTimeZone(deviceTimeZone);
  const timezoneUsed = journeyZone || deviceZone;
  const source = journeyZone ? 'JOURNEY_TIMEZONE' : 'DEVICE_LOCAL';
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  };
  if (timezoneUsed) options.timeZone = timezoneUsed;
  const parts = partMap(new Intl.DateTimeFormat('en-US', options).formatToParts(instant));
  const calendarDate = formatCalendarDate(parts.year, parts.month, parts.day);
  const clockTime = formatClockTime(parts.hour, parts.minute);
  return {
    source,
    timezoneUsed: timezoneUsed || '',
    usesJourneyTimezone: source === 'JOURNEY_TIMEZONE',
    clockHint: journeyZone ? journeyZone : '依裝置日期',
    calendarDate,
    clockTime,
    instant: instant.toISOString(),
    authoritative: false,
  };
}

export function clockMinutes(clockTime) {
  const match = String(clockTime || '').match(/^(\d{2}):(\d{2})/);
  if (!match) return null;
  return (Number(match[1]) * 60) + Number(match[2]);
}
